/**
 * The case's comments and activity (22 Oct, Rusty, demo case 11150): what the
 * people on the case wrote, and "Add a comment" for anyone who sees the case
 * (the manager, the requester, the teams with a step, approvers, admins).
 *
 * A comment is a public journal note on the case's anchor request in ITSM
 * (Access - New user, the Change case, the Leaver), written by the same
 * comment path the ticket page uses; the server checks the reader may see the
 * case. The box is the shared one, yellow while empty like every required
 * field; no files here (the requests themselves take them).
 */

import { ErrorState, LoadingState, SectionCard } from "@/components/common";
import { ComposeForm, TicketActivity, useFeedbackToast } from "@/features/tickets";
import { useLanguage, useT } from "@/i18n";

import { useAccessCaseActivity, useAddAccessCaseComment } from "../api";

export function CaseActivity({ caseId }: { caseId: string }) {
  const { t } = useT("tickets");
  const { language } = useLanguage();
  const toast = useFeedbackToast();
  const query = useAccessCaseActivity(caseId);
  const add = useAddAccessCaseComment(caseId);
  const activity = query.data;

  return (
    <SectionCard title={t("access.activity.title")}>
      <div className="flex min-w-0 flex-col gap-4">
        {query.isPending ? (
          <LoadingState size="sm" inline />
        ) : query.isError || activity === undefined ? (
          <ErrorState variant="inline" error={query.error} onRetry={() => void query.refetch()} />
        ) : (
          <TicketActivity entries={activity.items ?? []} />
        )}
        {activity?.can_comment ? (
          <ComposeForm
            name="case-comment"
            label={t("access.activity.label")}
            hint={t("access.activity.hint", { number: activity.ticket_number ?? "—" })}
            placeholder={t("access.activity.placeholder")}
            submitLabel={t("comment.send")}
            onSubmit={(body) =>
              add
                .mutateAsync({ body, body_lang: language })
                .then(() => toast.success(t("access.activity.posted")))
            }
            pending={add.isPending}
            error={add.error}
          />
        ) : null}
      </div>
    </SectionCard>
  );
}
